import { AlertCircle, CheckCircle2 } from "lucide-react";
import { Checkbox } from "@/components/ui/Checkbox";
import { cn } from "@/lib/utils";

/**
 * The per-section "I've reviewed this" checkbox.
 *
 * At module scope, not inside the page component: a component declared inside
 * another one is a new type on every render, so React unmounts and remounts the
 * whole box — and drops focus out of the checkbox — every time the page
 * re-renders, which `mode: "onChange"` makes happen on every keystroke.
 *
 * @param {object} reviewed - the page's per-section reviewed map
 * @param {boolean} showReviewHint - true once a Submit was blocked; turns the unchecked boxes red
 * @param {(key: string) => void} onToggle
 */
const ReviewConfirmBox = ({ sectionKey, reviewed, showReviewHint, onToggle }) => {
  const isReviewed = !!reviewed[sectionKey];
  const isMissing = showReviewHint && !isReviewed;

  return (
    <div className="mt-4">
      {isMissing && (
        <div className="mb-2 flex items-center gap-2 rounded-lg border border-tone-rose/30 bg-tone-rose-soft px-3 py-2 text-xs font-medium text-tone-rose">
          <AlertCircle size={14} className="shrink-0" />
          Please review this section and confirm it before submitting.
        </div>
      )}
      <div
        className={cn(
          "flex items-center justify-between gap-2 rounded-lg border px-3 py-2 transition-colors",
          isReviewed ? "border-tone-emerald/30 bg-tone-emerald-soft" : isMissing ? "border-tone-rose/50" : "border-border-primary",
        )}
      >
        <label htmlFor={`review-${sectionKey}`} className="flex flex-1 items-center gap-2 cursor-pointer select-none">
          <Checkbox
            id={`review-${sectionKey}`}
            checked={isReviewed}
            onCheckedChange={() => onToggle(sectionKey)}
            className={isReviewed ? "border-tone-emerald data-checked:border-tone-emerald data-checked:bg-tone-emerald" : ""}
          />
          <span className="text-sm text-text-primary">
            {isReviewed ? "Reviewed — information confirmed as accurate." : "I've reviewed this section and confirm the information is accurate."}
          </span>
        </label>
        {isReviewed && <CheckCircle2 size={16} className="shrink-0 text-tone-emerald" />}
      </div>
    </div>
  );
};

export default ReviewConfirmBox;
